import React, { useEffect, useState } from 'react';
import {
  Box, Grid, TextField, Typography, FormControlLabel, Checkbox,
  Select, MenuItem, InputLabel, FormControl,
  FormGroup, RadioGroup, Radio, CircularProgress, Stack, Button,
} from '@mui/material';
import PublicIcon from '@mui/icons-material/Public';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import CoronavirusIcon from '@mui/icons-material/Coronavirus';
import { fieldSx, yesNoLabelSx, SectionCard } from '../formTheme';
import { travelerScreeningAPI, publicSelfScreenAPI } from '../../../services/api';
import {
  tPublic,
  PublicLang,
  SYMPTOM_I18N_KEYS,
  EXPOSURE_I18N_KEYS,
} from '../publicScreenI18n';

/** Same ids/titles as db/generate_ebola_traveler_screening_flow.py */
const EBOLA_DEPARTURE_COUNTRIES: { id: string; title: string }[] = [
  { id: 'angola', title: 'Angola' },
  { id: 'belgium', title: 'Belgium' },
  { id: 'burundi', title: 'Burundi' },
  { id: 'cameroon', title: 'Cameroon' },
  { id: 'car', title: 'Central African Republic' },
  { id: 'chad', title: 'Chad' },
  { id: 'china', title: 'China' },
  { id: 'congo_rep', title: 'Congo (Republic)' },
  { id: 'drc', title: 'Congo (DRC)' },
  { id: 'egypt', title: 'Egypt' },
  { id: 'ethiopia', title: 'Ethiopia' },
  { id: 'france', title: 'France' },
  { id: 'germany', title: 'Germany' },
  { id: 'india', title: 'India' },
  { id: 'kenya', title: 'Kenya' },
  { id: 'nigeria', title: 'Nigeria' },
  { id: 'rwanda', title: 'Rwanda' },
  { id: 'south_sudan', title: 'South Sudan' },
  { id: 'sudan', title: 'Sudan' },
  { id: 'tanzania', title: 'Tanzania' },
  { id: 'uganda', title: 'Uganda' },
  { id: 'uk', title: 'United Kingdom' },
  { id: 'usa', title: 'United States' },
  { id: 'zambia', title: 'Zambia' },
  { id: 'other', title: 'Other' },
];

const EBOLA_SYMPTOM_OPTIONS = [
  { key: 'headache', label: 'Headache' },
  { key: 'muscle_joint_pain', label: 'Muscle or joint pain' },
  { key: 'vomiting_nausea', label: 'Vomiting or nausea' },
  { key: 'diarrhoea', label: 'Diarrhoea' },
  { key: 'abdominal_pain', label: 'Abdominal pain' },
  { key: 'bleeding_bruising', label: 'Bleeding or bruising' },
  { key: 'haemorrhagic_rash', label: 'Haemorrhagic rash or petechiae' },
  { key: 'conjunctival', label: 'Conjunctival injection (red eyes)' },
  { key: 'sore_throat', label: 'Sore throat' },
  { key: 'difficulty_swallowing', label: 'Difficulty swallowing' },
  { key: 'altered_consciousness', label: 'Altered consciousness' },
  { key: 'none', label: 'None of the above' },
];

const EBOLA_EXPOSURE_OPTIONS = [
  { key: 'ebola_contact_confirmed_case', label: 'Contact with a confirmed or suspected Ebola case?' },
  { key: 'ebola_healthcare_lab_work', label: 'Healthcare or laboratory work in an affected area?' },
  { key: 'ebola_funeral_body_prep', label: 'Attended a funeral or body preparation?' },
  { key: 'ebola_contact_animals', label: 'Contact with bats, primates, or monkeys?' },
  { key: 'ebola_bushmeat_handling', label: 'Hunted, slaughtered, or handled bushmeat?' },
  { key: 'ebola_forest_cave_exposure', label: 'Exposure to forests or caves?' },
];

const FOREST = '#0b5c45';
const MINT = '#e3f5ee';
const LINE = '#d5e3dc';

interface Props {
  form: Record<string, unknown>;
  onChange: (field: string, value: unknown) => void;
  subTab: number;
  subTabLabels: string[];
  /** agent = PHO Add Client; public = traveler self-screen (phone required, no temp, 1 col) */
  variant?: 'agent' | 'public';
  /** Public form language */
  lang?: PublicLang;
}

const publicFieldSx = {
  width: '100%',
  '& .MuiOutlinedInput-root': { borderRadius: '12px', minHeight: 48 },
  '& .MuiInputBase-input': { fontSize: 16, textAlign: 'left' },
  '& .MuiFormHelperText-root': { mx: 0, textAlign: 'left' },
};

/** Equal-width choice buttons — CSS grid only, no ToggleButtonGroup edge quirks */
const ChoiceRow: React.FC<{
  options: { value: string; label: string }[];
  value: string;
  onPick: (v: string) => void;
  cols: number;
}> = ({ options, value, onPick, cols }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`,
      gap: 1,
      width: '100%',
    }}
  >
    {options.map((opt) => {
      const selected = value === opt.value;
      return (
        <Button
          key={opt.value}
          type="button"
          fullWidth
          disableElevation
          onClick={() => onPick(opt.value)}
          sx={{
            m: 0,
            minHeight: 48,
            minWidth: 0,
            px: 1,
            borderRadius: '12px',
            textTransform: 'none',
            fontWeight: 700,
            fontSize: 14,
            justifyContent: 'center',
            border: `1.5px solid ${selected ? '#1a8f6a' : LINE}`,
            bgcolor: selected ? MINT : '#f7fbf9',
            color: selected ? FOREST : '#3d5a50',
            '&:hover': {
              bgcolor: selected ? MINT : '#eef6f2',
              borderColor: selected ? '#1a8f6a' : '#b9cfc5',
            },
          }}
        >
          {opt.label}
        </Button>
      );
    })}
  </Box>
);

const PublicSection: React.FC<{ title: string; icon: React.ReactNode; children: React.ReactNode }> = ({
  title, icon, children,
}) => (
  <Box
    sx={{
      width: '100%',
      boxSizing: 'border-box',
      border: `1px solid ${LINE}`,
      borderRadius: '14px',
      bgcolor: '#fff',
      p: 2,
      mb: 1.5,
    }}
  >
    <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.75, width: '100%' }}>
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: '10px',
          bgcolor: MINT,
          color: FOREST,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Typography sx={{ fontWeight: 700, fontSize: 15, color: '#0c1f1a', textAlign: 'left' }}>
        {title}
      </Typography>
    </Stack>
    <Stack spacing={1.5} sx={{ width: '100%', alignItems: 'stretch' }}>
      {children}
    </Stack>
  </Box>
);

/**
 * Same questions as WhatsApp Ebola Flow.
 * Agent: 2-col + phone + temperature. Public: phone required, no temp, 1 field/row, flush edges.
 */
const HealthFormStep: React.FC<Props> = ({ form, onChange, variant = 'agent', lang = 'en' }) => {
  const isPublic = variant === 'public';
  const L = (key: string) => (isPublic ? tPublic(lang, key) : key);
  const str = (key: string) => (form[key] as string) ?? '';
  const mode = str('ebola_mode_of_transport').toUpperCase() as '' | 'AIR' | 'SHIP' | 'ROAD';
  const ebolaSymptoms = (form.ebola_symptoms as string[]) || [];

  const [entryPoints, setEntryPoints] = useState<{ id: string; title: string }[]>([]);
  const [poeLoading, setPoeLoading] = useState(false);
  const [poeError, setPoeError] = useState<string | null>(null);

  useEffect(() => {
    if (!mode || !['AIR', 'SHIP', 'ROAD'].includes(mode)) {
      setEntryPoints([]);
      return;
    }
    let cancelled = false;
    setPoeLoading(true);
    setPoeError(null);
    const fetchPoes = isPublic
      ? publicSelfScreenAPI.getEntryPoints(mode)
      : travelerScreeningAPI.getEntryPoints(mode);
    fetchPoes
      .then((res) => {
        if (cancelled) return;
        setEntryPoints(res.items || []);
      })
      .catch(() => {
        if (cancelled) return;
        setEntryPoints([]);
        setPoeError('Could not load points of entry');
      })
      .finally(() => {
        if (!cancelled) setPoeLoading(false);
      });
    return () => { cancelled = true; };
  }, [mode, isPublic]);

  const toggleEbolaSymptom = (key: string, checked: boolean) => {
    if (key === 'none') {
      onChange('ebola_symptoms', checked ? ['none'] : []);
      return;
    }
    let next = ebolaSymptoms.filter((s) => s !== 'none');
    if (checked) next = [...next, key];
    else next = next.filter((s) => s !== key);
    onChange('ebola_symptoms', next);
  };

  const setMode = (value: string) => {
    const m = value.toUpperCase();
    onChange('ebola_mode_of_transport', m);
    onChange('ebola_point_of_entry', '');
    onChange('ebola_flight_number', '');
    onChange('ebola_vessel_number', '');
    onChange('ebola_vehicle_number', '');
    onChange('ebola_seat_number', '');
  };

  const transportLabel =
    mode === 'AIR' ? L('flight') : mode === 'SHIP' ? L('vessel') : mode === 'ROAD' ? L('vehicle') : L('flight');
  const transportKey =
    mode === 'AIR' ? 'ebola_flight_number' : mode === 'SHIP' ? 'ebola_vessel_number' : 'ebola_vehicle_number';

  /* ── Public: single column Stack, every control width 100% ── */
  if (isPublic) {
    return (
      <Box sx={{ width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
        <PublicSection title={L('demographics')} icon={<PublicIcon fontSize="small" />}>
          <TextField
            fullWidth required sx={publicFieldSx}
            label={L('full_name')}
            value={str('full_name')}
            onChange={(e) => onChange('full_name', e.target.value)}
          />
          <TextField
            fullWidth required sx={publicFieldSx}
            label={L('age')}
            type="number"
            inputProps={{ min: 0, max: 120 }}
            value={str('age')}
            onChange={(e) => onChange('age', e.target.value)}
          />
          <FormControl fullWidth required sx={publicFieldSx}>
            <InputLabel>{L('gender')}</InputLabel>
            <Select
              value={str('sex')}
              label={L('gender')}
              onChange={(e) => onChange('sex', e.target.value)}
            >
              <MenuItem value="male">{L('male')}</MenuItem>
              <MenuItem value="female">{L('female')}</MenuItem>
              <MenuItem value="other">{L('other')}</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth required sx={publicFieldSx}
            label={L('nationality')}
            value={str('nationality')}
            onChange={(e) => onChange('nationality', e.target.value)}
          />
          <TextField
            fullWidth required sx={publicFieldSx}
            label={L('passport')}
            value={str('id_number')}
            onChange={(e) => onChange('id_number', e.target.value)}
          />
          <TextField
            fullWidth required sx={publicFieldSx}
            label={L('country_code')}
            value={str('phone_country_code')}
            onChange={(e) => onChange('phone_country_code', e.target.value)}
            helperText={L('country_code_hint')}
            placeholder="+254"
            inputProps={{ inputMode: 'tel' }}
          />
          <TextField
            fullWidth required sx={publicFieldSx}
            label={L('phone')}
            value={str('phone_number')}
            onChange={(e) => onChange('phone_number', e.target.value)}
            helperText={L('phone_hint')}
            placeholder="712345678"
            inputProps={{ inputMode: 'tel' }}
          />
          <FormControl fullWidth required sx={publicFieldSx}>
            <InputLabel>{L('departure')}</InputLabel>
            <Select
              value={str('ebola_country_of_departure')}
              label={L('departure')}
              onChange={(e) => {
                onChange('ebola_country_of_departure', e.target.value);
                if (e.target.value !== 'other') onChange('ebola_other_country_specify', '');
              }}
            >
              {EBOLA_DEPARTURE_COUNTRIES.map((c) => (
                <MenuItem key={c.id} value={c.id}>{c.title}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {str('ebola_country_of_departure') === 'other' && (
            <TextField
              fullWidth required sx={publicFieldSx}
              label={L('country_name')}
              value={str('ebola_other_country_specify')}
              onChange={(e) => onChange('ebola_other_country_specify', e.target.value)}
            />
          )}
          <Box sx={{ width: '100%' }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#3d5a50', mb: 1, textAlign: 'left' }}>
              {L('mode')}
            </Typography>
            <ChoiceRow
              cols={3}
              value={mode}
              onPick={setMode}
              options={[
                { value: 'AIR', label: L('air') },
                { value: 'SHIP', label: L('ship') },
                { value: 'ROAD', label: L('road') },
              ]}
            />
          </Box>
        </PublicSection>

        <PublicSection title={L('travel')} icon={<TravelExploreIcon fontSize="small" />}>
          <FormControl fullWidth required sx={publicFieldSx} disabled={!mode || poeLoading}>
            <InputLabel>{L('poe')}</InputLabel>
            <Select
              value={str('ebola_point_of_entry')}
              label={L('poe')}
              onChange={(e) => onChange('ebola_point_of_entry', e.target.value)}
            >
              {entryPoints.map((p) => (
                <MenuItem key={p.id} value={p.id}>{p.title}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {poeLoading && (
            <Stack direction="row" spacing={1} alignItems="center">
              <CircularProgress size={14} />
              <Typography variant="caption" color="text.secondary">{L('poe_loading')}</Typography>
            </Stack>
          )}
          {!mode && (
            <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'left' }}>
              {L('poe_need_mode')}
            </Typography>
          )}
          {poeError && (
            <Typography variant="caption" color="error" sx={{ textAlign: 'left' }}>{L('poe_error')}</Typography>
          )}
          {!!mode && (
            <TextField
              fullWidth required sx={publicFieldSx}
              label={transportLabel}
              value={str(transportKey)}
              onChange={(e) => onChange(transportKey, e.target.value)}
            />
          )}
          {(mode === 'AIR' || mode === 'SHIP') && (
            <TextField
              fullWidth required sx={publicFieldSx}
              label={L('seat')}
              value={str('ebola_seat_number')}
              onChange={(e) => onChange('ebola_seat_number', e.target.value)}
            />
          )}
          <Box sx={{ width: '100%' }}>
            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#3d5a50', mb: 1, textAlign: 'left' }}>
              {L('visited_ug_drc')}
            </Typography>
            <ChoiceRow
              cols={2}
              value={str('ebola_visited_affected_countries')}
              onPick={(v) => onChange('ebola_visited_affected_countries', v)}
              options={[
                { value: 'yes', label: L('yes') },
                { value: 'no', label: L('no') },
              ]}
            />
          </Box>
        </PublicSection>

        {str('ebola_visited_affected_countries') === 'yes' && (
          <PublicSection title={L('exposure')} icon={<TravelExploreIcon fontSize="small" />}>
            {EBOLA_EXPOSURE_OPTIONS.map(({ key }) => (
              <Box key={key} sx={{ width: '100%' }}>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#3d5a50', mb: 1, textAlign: 'left' }}>
                  {L(EXPOSURE_I18N_KEYS[key] || key)}
                </Typography>
                <ChoiceRow
                  cols={2}
                  value={str(key)}
                  onPick={(v) => onChange(key, v)}
                  options={[
                    { value: 'yes', label: L('yes') },
                    { value: 'no', label: L('no') },
                  ]}
                />
              </Box>
            ))}
          </PublicSection>
        )}

        <PublicSection title={L('symptoms')} icon={<CoronavirusIcon fontSize="small" />}>
          <Typography sx={{ fontSize: 12, color: '#6b857c', mb: 0.5, mt: -0.5, textAlign: 'left' }}>
            {L('symptoms_hint')}
          </Typography>
          <Box
            sx={{
              width: '100%',
              borderRadius: '12px',
              bgcolor: '#f4faf7',
              border: `1px solid ${LINE}`,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                width: '100%',
              }}
            >
              {EBOLA_SYMPTOM_OPTIONS.filter((s) => s.key !== 'none').map(({ key }, idx) => {
                const checked = ebolaSymptoms.includes(key);
                const isLeft = idx % 2 === 0;
                return (
                  <Box
                    key={key}
                    component="button"
                    type="button"
                    onClick={() => toggleEbolaSymptom(key, !checked)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.75,
                      width: '100%',
                      minWidth: 0,
                      m: 0,
                      px: 1.25,
                      py: 1.1,
                      border: 'none',
                      borderRight: isLeft ? `1px solid ${LINE}` : 'none',
                      borderBottom: `1px solid ${LINE}`,
                      bgcolor: checked ? MINT : 'transparent',
                      cursor: 'pointer',
                      textAlign: 'left',
                      font: 'inherit',
                      WebkitTapHighlightColor: 'transparent',
                      '&:active': { bgcolor: checked ? '#d4efe3' : '#eaf5ef' },
                    }}
                  >
                    <Box
                      aria-hidden
                      sx={{
                        width: 16,
                        height: 16,
                        flexShrink: 0,
                        borderRadius: '4px',
                        border: checked ? 'none' : `1.5px solid #8aa899`,
                        bgcolor: checked ? FOREST : '#fff',
                        display: 'grid',
                        placeItems: 'center',
                        color: '#fff',
                        fontSize: 11,
                        fontWeight: 800,
                        lineHeight: 1,
                      }}
                    >
                      {checked ? '✓' : ''}
                    </Box>
                    <Typography
                      component="span"
                      sx={{
                        fontSize: 12.5,
                        fontWeight: checked ? 600 : 500,
                        color: checked ? FOREST : '#1e293b',
                        lineHeight: 1.25,
                        textAlign: 'left',
                      }}
                    >
                      {L(SYMPTOM_I18N_KEYS[key] || key)}
                    </Typography>
                  </Box>
                );
              })}
            </Box>
            {(() => {
              const noneChecked = ebolaSymptoms.includes('none');
              return (
                <Box
                  component="button"
                  type="button"
                  onClick={() => toggleEbolaSymptom('none', !noneChecked)}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.75,
                    width: '100%',
                    m: 0,
                    px: 1.25,
                    py: 1.25,
                    border: 'none',
                    bgcolor: noneChecked ? MINT : '#fff',
                    cursor: 'pointer',
                    textAlign: 'left',
                    font: 'inherit',
                    WebkitTapHighlightColor: 'transparent',
                  }}
                >
                  <Box
                    aria-hidden
                    sx={{
                      width: 16,
                      height: 16,
                      flexShrink: 0,
                      borderRadius: '4px',
                      border: noneChecked ? 'none' : `1.5px solid #8aa899`,
                      bgcolor: noneChecked ? FOREST : '#fff',
                      display: 'grid',
                      placeItems: 'center',
                      color: '#fff',
                      fontSize: 11,
                      fontWeight: 800,
                      lineHeight: 1,
                    }}
                  >
                    {noneChecked ? '✓' : ''}
                  </Box>
                  <Typography
                    component="span"
                    sx={{
                      fontSize: 12.5,
                      fontWeight: 600,
                      color: noneChecked ? FOREST : '#3d5a50',
                      textAlign: 'left',
                    }}
                  >
                    {L('sym_none')}
                  </Typography>
                </Box>
              );
            })()}
          </Box>
        </PublicSection>
      </Box>
    );
  }

  /* ── Agent Add Client (unchanged 2-col layout) ── */
  const agentTransportLabel =
    mode === 'AIR' ? 'Flight number *' : mode === 'SHIP' ? 'Vessel number *' : mode === 'ROAD' ? 'Vehicle number *' : 'Transport number *';
  return (
    <Box>
      <SectionCard title="Demographics" icon={<PublicIcon fontSize="small" />}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" required sx={fieldSx}
              label="Full name *"
              value={str('full_name')}
              onChange={(e) => onChange('full_name', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              fullWidth size="small" required sx={fieldSx}
              label="Age *"
              type="number"
              inputProps={{ min: 0, max: 120 }}
              value={str('age')}
              onChange={(e) => onChange('age', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <FormControl fullWidth size="small" required sx={fieldSx}>
              <InputLabel>Gender *</InputLabel>
              <Select
                value={str('sex')}
                label="Gender *"
                onChange={(e) => onChange('sex', e.target.value)}
              >
                <MenuItem value="male">Male</MenuItem>
                <MenuItem value="female">Female</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" required sx={fieldSx}
              label="Nationality *"
              value={str('nationality')}
              onChange={(e) => onChange('nationality', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" required sx={fieldSx}
              label="Passport/ID Number *"
              value={str('id_number')}
              onChange={(e) => onChange('id_number', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" sx={fieldSx}
              label="Phone number"
              value={str('phone_number')}
              onChange={(e) => onChange('phone_number', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth size="small" required sx={fieldSx}
              label="Temperature (°C) *"
              type="number"
              inputProps={{ step: 0.1, min: 30, max: 45 }}
              value={str('temperature')}
              onChange={(e) => onChange('temperature', e.target.value)}
              helperText="Measured at checkpoint"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small" required sx={fieldSx}>
              <InputLabel>Country of departure *</InputLabel>
              <Select
                value={str('ebola_country_of_departure')}
                label="Country of departure *"
                onChange={(e) => {
                  onChange('ebola_country_of_departure', e.target.value);
                  if (e.target.value !== 'other') onChange('ebola_other_country_specify', '');
                }}
              >
                {EBOLA_DEPARTURE_COUNTRIES.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.title}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {str('ebola_country_of_departure') === 'other' && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" required sx={fieldSx}
                label="Country name *"
                value={str('ebola_other_country_specify')}
                onChange={(e) => onChange('ebola_other_country_specify', e.target.value)}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <FormControl required>
              <Typography sx={yesNoLabelSx}>Mode of transport *</Typography>
              <RadioGroup row value={mode} onChange={(e) => setMode(e.target.value)}>
                <FormControlLabel value="AIR" control={<Radio size="small" />} label="Air" />
                <FormControlLabel value="SHIP" control={<Radio size="small" />} label="Ship" />
                <FormControlLabel value="ROAD" control={<Radio size="small" />} label="Road" />
              </RadioGroup>
            </FormControl>
          </Grid>
        </Grid>
      </SectionCard>

      <SectionCard title="Travel Details" icon={<TravelExploreIcon fontSize="small" />}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small" required sx={fieldSx} disabled={!mode || poeLoading}>
              <InputLabel>Point of entry *</InputLabel>
              <Select
                value={str('ebola_point_of_entry')}
                label="Point of entry *"
                onChange={(e) => onChange('ebola_point_of_entry', e.target.value)}
              >
                {entryPoints.map((p) => (
                  <MenuItem key={p.id} value={p.id}>{p.title}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {poeLoading && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                <CircularProgress size={14} />
                <Typography variant="caption" color="text.secondary">Loading entry points…</Typography>
              </Box>
            )}
            {!mode && (
              <Typography variant="caption" color="text.secondary">Select mode of transport first</Typography>
            )}
            {poeError && (
              <Typography variant="caption" color="error">{poeError}</Typography>
            )}
          </Grid>
          {!!mode && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" required sx={fieldSx}
                label={agentTransportLabel}
                value={str(transportKey)}
                onChange={(e) => onChange(transportKey, e.target.value)}
              />
            </Grid>
          )}
          {(mode === 'AIR' || mode === 'SHIP') && (
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth size="small" required sx={fieldSx}
                label="Seat number *"
                value={str('ebola_seat_number')}
                onChange={(e) => onChange('ebola_seat_number', e.target.value)}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <FormControl required>
              <Typography sx={yesNoLabelSx}>
                Visited Uganda or DR Congo in the last 21 days? *
              </Typography>
              <RadioGroup
                row
                value={str('ebola_visited_affected_countries')}
                onChange={(e) => onChange('ebola_visited_affected_countries', e.target.value)}
              >
                <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
                <FormControlLabel value="no" control={<Radio size="small" />} label="No" />
              </RadioGroup>
            </FormControl>
          </Grid>
        </Grid>
      </SectionCard>

      {str('ebola_visited_affected_countries') === 'yes' && (
        <SectionCard title="Travel History / Exposure" icon={<TravelExploreIcon fontSize="small" />}>
          <Grid container spacing={1.5}>
            {EBOLA_EXPOSURE_OPTIONS.map(({ key, label }) => (
              <Grid item xs={12} sm={6} key={key}>
                <FormControl fullWidth required>
                  <Typography sx={yesNoLabelSx}>{label}</Typography>
                  <RadioGroup row value={str(key)} onChange={(e) => onChange(key, e.target.value)}>
                    <FormControlLabel value="yes" control={<Radio size="small" />} label="Yes" />
                    <FormControlLabel value="no" control={<Radio size="small" />} label="No" />
                  </RadioGroup>
                </FormControl>
              </Grid>
            ))}
          </Grid>
        </SectionCard>
      )}

      <SectionCard title="Symptoms (select all that apply) *" icon={<CoronavirusIcon fontSize="small" />}>
        <FormGroup>
          <Grid container>
            {EBOLA_SYMPTOM_OPTIONS.map(({ key, label }) => (
              <Grid item xs={12} sm={6} key={key}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={ebolaSymptoms.includes(key)}
                      onChange={(e) => toggleEbolaSymptom(key, e.target.checked)}
                      color="error"
                    />
                  }
                  label={label}
                />
              </Grid>
            ))}
          </Grid>
        </FormGroup>
      </SectionCard>
    </Box>
  );
};

export default HealthFormStep;
